// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { styled } from '@mui/material';
import { LinkComponent, createLink } from '@tanstack/react-router';

const VerticalNavigationAnchor = styled('a')(({ theme }) => ({
  whiteSpace: 'nowrap',
  textDecoration: 'none',
  justifyContent: 'start',
  paddingRight: theme.spacing(3),
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  color: theme.palette.text.secondary,
  fontSize: theme.typography.body2.fontSize,
  fontWeight: theme.typography.fontWeightMedium,
  transition: theme.transitions.create(['color']),
  [theme.breakpoints.down('md')]: {
    paddingLeft: theme.spacing(2),
    borderBottom: '2px solid transparent',
    '&.active': {
      borderBottomColor: theme.palette.primary.main,
    },
  },
  [theme.breakpoints.up('md')]: {
    borderRight: '2px solid transparent',
    '&.active': {
      borderRightColor: theme.palette.primary.main,
    },
  },
  '&.active, &:hover': {
    color: theme.palette.primary.main,
  },
}));

const Link = createLink(VerticalNavigationAnchor);

/**
 * A styled link component for navigating within the application. This
 * component is intended to be used in a vertical navigation menu.
 *
 * `resetScroll` defaults to `false` because every consumer of this component
 * is an in-page tab on a detail page (project, asset, item, location, etc.).
 * The user expects to stay at their current scroll position when switching
 * between tabs rather than being jumped back to the top of the page. Callers
 * can still opt back into the default scroll-to-top behavior by passing
 * `resetScroll` explicitly.
 */
export const VerticalNavigationLink: LinkComponent<
  typeof VerticalNavigationAnchor
> = (props) => {
  return <Link preload="intent" resetScroll={false} {...props} />;
};
